import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

const LANES = [-3.2, 0, 3.2];
const ROAD_WIDTH = 10.5;
const SEGMENT_LENGTH = 22;
const SEGMENTS = 16;
const TOKEN_POOL = 6;
const ENEMY_POOL = 4;
const CITY_POOL = 20;
const NITRO_DURATION = 4.5;
const POWERUP_POOL = 6;
const POLICE_DISTANCE = 900;
const BRIDGE_LENGTH = 300;
const ROAD_WORKS_LENGTH = 200;
const ROAD_WORKS_START_Z = -520;
const CROSSING_POOL = 1;
const SKY_RUN_DURATION = 4;
const SKY_TOKEN_POOL = 18;
const SKY_HEIGHT = 21;
const PORTAL_DURATION = 3;
const PORTAL_TOKEN_POOL = 18;
const CYBER_RING_POOL = 14;

function curveAt() {
  return 0;
}

const ENEMY_MIN_GAP = 18;

function keepEnemySeparated(enemyMeta, index) {
  const item = enemyMeta[index];

  for (let j = 0; j < enemyMeta.length; j += 1) {
    if (j === index) continue;

    const other = enemyMeta[j];

    if (
      item.lane === other.lane &&
      Math.abs(item.z - other.z) < ENEMY_MIN_GAP
    ) {
      // Keep this car behind the other one instead of letting
      // both occupy the same position.
      item.z =
        other.z -
        ENEMY_MIN_GAP -
        4 -
        Math.random() * 8;
    }
  }
}

function CarModel({ modelPath, color, scale = 1 }) {
  const { scene } = useGLTF(modelPath);
  const clone = useMemo(() => scene.clone(true), [scene]);

  useEffect(() => {
    clone.traverse((child) => {
      if (!child.isMesh) return;
      child.castShadow = false;
      child.receiveShadow = false;
      const mat = child.material?.clone?.() || child.material;
      if (mat) {
        child.material = mat;
        mat.envMapIntensity = 0.45;
        mat.metalness = Math.min(0.75, mat.metalness ?? 0.35);
        mat.roughness = Math.max(0.24, mat.roughness ?? 0.35);
        if (/body|paint|carpaint|exterior/i.test(child.name || '')) {
          mat.color?.set?.(color);
        }
        if (/mirror/i.test(child.name || '')) mat.color?.set?.('#06080d');
      }
    });
  }, [clone, color]);

  return (
    <group>
      <primitive object={clone} scale={scale} />
    </group>
  );
}

function EnemyCar({ carRef, color = '#151922' }) {
  return (
    <group ref={carRef}>
      {/* Нижняя часть кузова */}
      <mesh position={[0, 0.38, 0]}>
        <boxGeometry args={[1.45, 0.38, 2.75]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.08}
          metalness={0.62}
          roughness={0.3}
        />
      </mesh>

      {/* Капот */}
      <mesh
        position={[0, 0.56, -0.82]}
        rotation={[0.06, 0, 0]}
      >
        <boxGeometry args={[1.32, 0.18, 1.05]} />
        <meshStandardMaterial
          color={color}
          metalness={0.62}
          roughness={0.3}
        />
      </mesh>

      {/* Кабина */}
      <mesh position={[0, 0.78, 0.18]}>
        <boxGeometry args={[1.12, 0.5, 1.25]} />
        <meshStandardMaterial
          color="#182331"
          metalness={0.25}
          roughness={0.2}
        />
      </mesh>

      {/* Лобовое стекло */}
      <mesh
        position={[0, 0.82, -0.48]}
        rotation={[-0.32, 0, 0]}
      >
        <planeGeometry args={[1.0, 0.48]} />
        <meshStandardMaterial
          color="#2e5368"
          emissive="#102938"
          emissiveIntensity={0.25}
          roughness={0.1}
          metalness={0.15}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Заднее стекло */}
      <mesh
        position={[0, 0.82, 0.82]}
        rotation={[0.32, 0, 0]}
      >
        <planeGeometry args={[1.0, 0.42]} />
        <meshStandardMaterial
          color="#243b4d"
          roughness={0.12}
          metalness={0.15}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Задние фонари */}
      <mesh position={[-0.43, 0.48, 1.39]}>
        <boxGeometry args={[0.36, 0.12, 0.05]} />
        <meshStandardMaterial
          color="#ff244f"
          emissive="#ff244f"
          emissiveIntensity={3}
          toneMapped={false}
        />
      </mesh>

      <mesh position={[0.43, 0.48, 1.39]}>
        <boxGeometry args={[0.36, 0.12, 0.05]} />
        <meshStandardMaterial
          color="#ff244f"
          emissive="#ff244f"
          emissiveIntensity={3}
          toneMapped={false}
        />
      </mesh>

      {/* Колёса */}
      {[
        [-0.72, 0.25, -0.82],
        [0.72, 0.25, -0.82],
        [-0.72, 0.25, 0.87],
        [0.72, 0.25, 0.87],
      ].map(([x, y, z], i) => (
        <mesh
          key={i}
          position={[x, y, z]}
          rotation={[0, 0, Math.PI / 2]}
        >
          <cylinderGeometry args={[0.27, 0.27, 0.18, 10]} />
          <meshStandardMaterial
            color="#090b0f"
            roughness={0.85}
          />
        </mesh>
      ))}

      {/* Диски */}
      {[
        [-0.82, 0.25, -0.82],
        [0.82, 0.25, -0.82],
        [-0.82, 0.25, 0.87],
        [0.82, 0.25, 0.87],
      ].map(([x, y, z], i) => (
        <mesh
          key={`rim-${i}`}
          position={[x, y, z]}
          rotation={[0, 0, Math.PI / 2]}
        >
          <cylinderGeometry args={[0.14, 0.14, 0.03, 8]} />
          <meshStandardMaterial
            color="#7f8995"
            metalness={0.75}
            roughness={0.3}
          />
        </mesh>
      ))}
    </group>
  );
}

function JumpTruck({ truckRef }) {
  return (
    <group ref={truckRef}>
      <mesh position={[0, 0.62, -0.7]}>
        <boxGeometry args={[2.45, 0.9, 5.2]} />
        <meshStandardMaterial
          color="#e3a11d"
          metalness={0.35}
          roughness={0.42}
        />
      </mesh>

      <mesh position={[0, 1.15, -2.7]}>
        <boxGeometry args={[2.3, 1.8, 1.85]} />
        <meshStandardMaterial
          color="#f0b329"
          metalness={0.4}
          roughness={0.34}
        />
      </mesh>

      <mesh position={[0, 1.42, -3.64]}>
        <boxGeometry args={[1.72, 0.65, 0.06]} />
        <meshStandardMaterial
          color="#16304a"
          emissive="#16304a"
          emissiveIntensity={0.45}
          metalness={0.15}
          roughness={0.2}
        />
      </mesh>

      <mesh position={[0, 0.28, 2.65]} rotation={[-0.2, 0, 0]}>
        <boxGeometry args={[2.5, 0.16, 3.7]} />
        <meshStandardMaterial
          color="#626d7b"
          metalness={0.72}
          roughness={0.3}
        />
      </mesh>

      <mesh position={[-0.82, 0.63, 2.0]}>
        <boxGeometry args={[0.4, 0.2, 0.08]} />
        <meshStandardMaterial
          color="#ff244f"
          emissive="#ff244f"
          emissiveIntensity={3.2}
        />
      </mesh>

      <mesh position={[0.82, 0.63, 2.0]}>
        <boxGeometry args={[0.4, 0.2, 0.08]} />
        <meshStandardMaterial
          color="#ff244f"
          emissive="#ff244f"
          emissiveIntensity={3.2}
        />
      </mesh>

      {[-0.88, 0.88].map((x) => (
        <React.Fragment key={x}>
          <mesh position={[x, 0.28, 1.25]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.34, 0.34, 0.24, 10]} />
            <meshStandardMaterial color="#090b10" roughness={0.9} />
          </mesh>
          <mesh position={[x, 0.28, -2.3]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.34, 0.34, 0.24, 10]} />
            <meshStandardMaterial color="#090b10" roughness={0.9} />
          </mesh>
        </React.Fragment>
      ))}
    </group>
  );
}

function SkyArrowPickup({ pickupRef }) {
  return (
    <group ref={pickupRef}>
      {/* Bright turquoise up-arrow power-up. Kept light-weight for mobile. */}
      <mesh position={[0, 0.22, 0]}>
        <boxGeometry args={[0.22, 0.72, 0.16]} />
        <meshStandardMaterial
          color="#43F7FF"
          emissive="#00EFFF"
          emissiveIntensity={5.5}
          metalness={0.25}
          roughness={0.16}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, 0.72, 0]}>
        <coneGeometry args={[0.5, 0.72, 4]} />
        <meshStandardMaterial
          color="#8CFFFF"
          emissive="#00EFFF"
          emissiveIntensity={5.8}
          metalness={0.2}
          roughness={0.12}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

function SkyToken({ tokenRef }) {
  return (
    <group ref={tokenRef}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.4, 0.4, 0.12, 6]} />
        <meshStandardMaterial
          color="#FF3157"
          emissive="#FF174D"
          emissiveIntensity={4.2}
          metalness={0.2}
          roughness={0.2}
          toneMapped={false}
        />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.26, 0.045, 6, 12]} />
        <meshBasicMaterial color="#FF8AA0" toneMapped={false} />
      </mesh>
    </group>
  );
}

function SpacePlanet({ planetRef }) {
  return (
    <group ref={planetRef} position={[20, 29, -105]}>
      <mesh>
        <sphereGeometry args={[10, 24, 16]} />
        <meshStandardMaterial
          color="#5137A8"
          emissive="#20104E"
          emissiveIntensity={0.85}
          roughness={0.72}
          metalness={0.08}
        />
      </mesh>
      <mesh rotation={[0.42, 0.2, 0.22]}>
        <torusGeometry args={[14, 0.65, 8, 36]} />
        <meshBasicMaterial
          color="#43F7FF"
          transparent
          opacity={0.34}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}


function NeonPortal({ portalRef }) {
  return (
    <group ref={portalRef}>
      <mesh>
        <torusGeometry args={[1.45, 0.16, 8, 32]} />
        <meshBasicMaterial color="#C344FF" toneMapped={false} />
      </mesh>
      <mesh scale={0.82}>
        <torusGeometry args={[1.45, 0.055, 6, 28]} />
        <meshBasicMaterial color="#43F7FF" toneMapped={false} />
      </mesh>
      <mesh position={[0, 0, -0.03]}>
        <circleGeometry args={[1.25, 28]} />
        <meshBasicMaterial color="#6D1EFF" transparent opacity={0.18} toneMapped={false} />
      </mesh>
    </group>
  );
}

function CyberToken({ tokenRef }) {
  return (
    <group ref={tokenRef} scale={1.05}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.38, 0.38, 0.12, 6]} />
        <meshStandardMaterial color="#FF3157" emissive="#FF174D" emissiveIntensity={4.5} roughness={0.2} toneMapped={false} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.25, 0.045, 6, 12]} />
        <meshBasicMaterial color="#FF91A5" toneMapped={false} />
      </mesh>
    </group>
  );
}

function CyberRing({ ringRef, index }) {
  const c = index % 2 === 0 ? '#C344FF' : '#43F7FF';
  return (
    <group ref={ringRef}>
      <mesh position={[-5.15, 2.8, 0]}><boxGeometry args={[0.07, 5.6, 0.09]} /><meshBasicMaterial color={c} toneMapped={false} /></mesh>
      <mesh position={[5.15, 2.8, 0]}><boxGeometry args={[0.07, 5.6, 0.09]} /><meshBasicMaterial color={c} toneMapped={false} /></mesh>
      <mesh position={[0, 5.6, 0]}><boxGeometry args={[10.35, 0.07, 0.09]} /><meshBasicMaterial color={c} toneMapped={false} /></mesh>
    </group>
  );
}

function CyberSpace({ cyberRef, ringRefs }) {
  return (
    <group ref={cyberRef} visible={false}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.09, -75]}>
        <planeGeometry args={[12, 190]} />
        <meshBasicMaterial color="#010106" />
      </mesh>
      {[-4.7, -3.2, 0, 3.2, 4.7].map((x, i) => (
        <mesh key={x} position={[x, 0.025, -75]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[i === 2 ? 0.035 : 0.055, 190]} />
          <meshBasicMaterial color={i % 2 ? '#C344FF' : '#43F7FF'} transparent opacity={0.78} toneMapped={false} />
        </mesh>
      ))}
      {Array.from({ length: CYBER_RING_POOL }).map((_, i) => (
        <CyberRing key={i} index={i} ringRef={(el) => { ringRefs.current[i] = el; }} />
      ))}
    </group>
  );
}

function NitroPickup({ pickupRef }) {
  return (
    <group ref={pickupRef}>
      {/* Основная капсула */}
      <mesh>
        <capsuleGeometry args={[0.22, 0.62, 6, 10]} />
        <meshStandardMaterial
          color="#43FF3D"
          emissive="#18FF00"
          emissiveIntensity={5.5}
          metalness={0.35}
          roughness={0.12}
          toneMapped={false}
        />
      </mesh>

      {/* Верх капсулы */}
      <mesh position={[0, 0.46, 0]}>
        <cylinderGeometry args={[0.13, 0.13, 0.15, 10]} />
        <meshStandardMaterial
          color="#D7FFD3"
          emissive="#72FF68"
          emissiveIntensity={3.5}
          metalness={0.55}
          roughness={0.15}
          toneMapped={false}
        />
      </mesh>

      {/* Зелёное свечение на дороге */}
      <pointLight
        color="#18FF00"
        intensity={1.5}
        distance={2.6}
        decay={2}
      />
    </group>
  );
}

/*function PoliceCar({ policeRef }) {
  return (
    <group ref={policeRef}>
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[1.35, 0.5, 2.5]} />
        <meshStandardMaterial color="#101722" metalness={0.65} roughness={0.28} />
      </mesh>
      <mesh position={[0, 0.8, -0.05]}>
        <boxGeometry args={[1.05, 0.4, 1.18]} />
        <meshStandardMaterial color="#243447" metalness={0.25} roughness={0.25} />
      </mesh>
      <mesh position={[-0.24, 1.05, -0.1]}>
        <boxGeometry args={[0.35, 0.08, 0.22]} />
        <meshStandardMaterial color="#2988ff" emissive="#2988ff" emissiveIntensity={5} />
      </mesh>
      <mesh position={[0.24, 1.05, -0.1]}>
        <boxGeometry args={[0.35, 0.08, 0.22]} />
        <meshStandardMaterial color="#ff244f" emissive="#ff244f" emissiveIntensity={5} />
      </mesh>
      <pointLight position={[-0.35, 1.15, 0]} intensity={8} distance={10} color="#2988ff" />
      <pointLight position={[0.35, 1.15, 0]} intensity={8} distance={10} color="#ff244f" />
    </group>
  );
}*/

function Tunnel({ tunnelRef }) {
  return (
    <group ref={tunnelRef}>
      {[-ROAD_WIDTH / 2 - 0.6, ROAD_WIDTH / 2 + 0.6].map((x) => (
        <mesh key={x} position={[x, 2.6, 0]}>
          <boxGeometry args={[0.6, 5.2, 18]} />
          <meshStandardMaterial color="#111a28" roughness={0.8} metalness={0.15} />
        </mesh>
      ))}
      <mesh position={[0, 5.15, 0]}>
        <boxGeometry args={[ROAD_WIDTH + 1.8, 0.45, 18]} />
        <meshStandardMaterial color="#101722" roughness={0.75} metalness={0.12} />
      </mesh>
      {[-6, 0, 6].map((z) => (
        <mesh key={z} position={[0, 4.82, z]}>
          <boxGeometry args={[5.8, 0.06, 0.22]} />
          <meshBasicMaterial color="#6ef6ff" />
        </mesh>
      ))}
    </group>
  );
}

function Bridge({ bridgeRef }) {
  const poles = Array.from(
    { length: 31 },
    (_, i) => -150 + i * 10
  );

  return (
    <group ref={bridgeRef}>
      {[-ROAD_WIDTH / 2 - 0.8, ROAD_WIDTH / 2 + 0.8].map((x) => (
        <React.Fragment key={x}>
          <mesh position={[x, 0.55, 0]}>
            <boxGeometry args={[0.18, 1.1, BRIDGE_LENGTH]} />
            <meshStandardMaterial
              color="#596776"
              metalness={0.55}
              roughness={0.35}
            />
          </mesh>

          {poles.map((z) => (
            <mesh key={z} position={[x, 2.3, z]}>
              <boxGeometry args={[0.16, 3.6, 0.16]} />
              <meshStandardMaterial
                color="#607286"
                metalness={0.55}
                roughness={0.32}
              />
            </mesh>
          ))}
        </React.Fragment>
      ))}

      <mesh position={[0, -0.03, 0]}>
        <boxGeometry
          args={[ROAD_WIDTH + 1.6, 0.18, BRIDGE_LENGTH]}
        />
        <meshStandardMaterial
          color="#171d26"
          roughness={0.9}
        />
      </mesh>
    </group>
  );
}

function RoadWorks({ roadWorksRef }) {
  const conePositions = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        z: ROAD_WORKS_LENGTH / 2 - 10 - i * 14,
        x: i % 2 === 0 ? -0.48 : 0.48,
      })),
    []
  );

  return (
    <group ref={roadWorksRef}>
      {/* Конусы вдоль закрытой полосы */}
      {conePositions.map((item, i) => (
        <group
          key={i}
          position={[item.x, 0, item.z]}
        >
          <mesh position={[0, 0.24, 0]}>
            <coneGeometry args={[0.18, 0.48, 6]} />
            <meshStandardMaterial
              color="#ff8a1f"
              emissive="#ff5a00"
              emissiveIntensity={1.1}
              roughness={0.65}
            />
          </mesh>

          <mesh position={[0, 0.025, 0]}>
            <boxGeometry args={[0.42, 0.05, 0.42]} />
            <meshStandardMaterial
              color="#15171c"
              roughness={0.9}
            />
          </mesh>
        </group>
      ))}

      {/* Первый предупреждающий барьер */}
      <group position={[0, 0, ROAD_WORKS_LENGTH / 2 - 25]}>
        <mesh position={[0, 0.5, 0]}>
          <boxGeometry args={[2.25, 0.5, 0.18]} />
          <meshStandardMaterial
            color="#ff8a1f"
            emissive="#ff5a00"
            emissiveIntensity={0.7}
            roughness={0.55}
          />
        </mesh>

        <mesh position={[-0.85, 0.25, 0]}>
          <boxGeometry args={[0.12, 0.5, 0.12]} />
          <meshStandardMaterial color="#59616d" />
        </mesh>

        <mesh position={[0.85, 0.25, 0]}>
          <boxGeometry args={[0.12, 0.5, 0.12]} />
          <meshStandardMaterial color="#59616d" />
        </mesh>
      </group>

      {/* Барьеры внутри ремонтируемой полосы */}
      {[40, 100, 160, 220].map((offset) => (
        <mesh
          key={offset}
          position={[
            0,
            0.32,
            ROAD_WORKS_LENGTH / 2 - offset,
          ]}
        >
          <boxGeometry args={[2.1, 0.64, 0.6]} />
          <meshStandardMaterial
            color="#e56b1f"
            emissive="#a6320c"
            emissiveIntensity={0.35}
            roughness={0.7}
          />
        </mesh>
      ))}
    </group>
  );
}

function PedestrianCrossing({ crossingRef }) {
  const stripes = Array.from({ length: 9 });

  return (
    <group ref={crossingRef}>
      {stripes.map((_, i) => (
        <mesh
          key={i}
          position={[
            -4.2 + i * 1.05,
            0.018,
            0,
          ]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <planeGeometry args={[0.55, 4.2]} />

          <meshBasicMaterial
            color="#dfe5ec"
            transparent
            opacity={0.65}
          />
        </mesh>
      ))}
    </group>
  );
}

/*function Token({ tokenRef }) {
  return (
    <group ref={tokenRef}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.38, 0.38, 0.12, 6]} />
        <meshStandardMaterial
          color="#E94B5F"
          emissive="#D9364E"
          emissiveIntensity={1.6}
          metalness={0.38}
          roughness={0.28}
        />
      </mesh>

      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.22, 0.05, 8, 20]} />
        <meshBasicMaterial color="#FF7A88" />
      </mesh>
    </group>
  );
}*/
function Token({ tokenRef }) {
  return (
    <group ref={tokenRef}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.38, 0.38, 0.12, 6]} />
        <meshStandardMaterial
          color="#FF3157"
          emissive="#FF174D"
          emissiveIntensity={3.8}
          metalness={0.2}
          roughness={0.22}
          toneMapped={false}
        />
      </mesh>

      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.25, 0.045, 6, 12]} />
        <meshBasicMaterial
          color="#FF6B85"
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

function RareToken({ tokenRef }) {
  return (
    <group ref={tokenRef} scale={1.2}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.38, 0.38, 0.14, 6]} />
        <meshStandardMaterial
          color="#FFD84D"
          emissive="#FFB800"
          emissiveIntensity={4.5}
          metalness={0.55}
          roughness={0.18}
          toneMapped={false}
        />
      </mesh>

      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.25, 0.045, 6, 12]} />
        <meshBasicMaterial
          color="#FFF29A"
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

function Lamp({ side = 1 }) {
  return (
    <group>
      <mesh position={[0, 1.55, 0]}>
        <cylinderGeometry args={[0.035, 0.05, 3.1, 6]} />
        <meshStandardMaterial color="#2e3847" metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[side * -0.2, 3.02, 0]}>
        <boxGeometry args={[0.55, 0.08, 0.11]} />
        <meshStandardMaterial color="#7dfaff" emissive="#55ebff" emissiveIntensity={4.2} />
      </mesh>
    </group>
  );
}

function CityBlock({ index }) {
  const side = index % 2 === 0 ? -1 : 1;
  const width = 3 + (index % 4) * 0.7;
  const height = 4.5 + (index % 5) * 1.35;
  const depth = 5.8 + (index % 3) * 1.7;
  const tone = index % 3 === 0
    ? '#1c2940'
    : index % 3 === 1
      ? '#2a1d32'
      : '#172b32';
  return (
    <group>
      <mesh position={[0, height / 2 - 0.1, 0]}>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial color={tone} roughness={0.82} metalness={0.1} />
      </mesh>
      {Array.from({ length: 4 }).map((_, row) => (
        <group key={row} position={[side * -width * 0.51, 1 + row * 1.15, 0]}>
          {Array.from({ length: 3 }).map((__, col) => (
            <mesh key={col} position={[0, 0, -1.5 + col * 1.5]} rotation={[0, Math.PI / 2, 0]}>
              <planeGeometry args={[0.52, 0.28]} />
              <meshBasicMaterial color={row % 2 ? '#ffd184' : '#69e9ff'} transparent opacity={0.55 + ((index + col) % 3) * 0.12} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

function RaceScene({
  running,
  laneTarget,
  activeCar,
  activeColor,
  onSpeed,
  onToken,
  onCrash,
  quality,
  onPowerState,
}) {
  const root = useRef();
  const cameraX = useRef(0);
  const carGroup = useRef();
  const roadRefs = useRef([]);
  const laneRefs = useRef([]);
  const tokenRefs = useRef([]);
  const rareTokenRef = useRef();
  const skyArrowRef = useRef();
  const skyTokenRefs = useRef([]);
  const planetRef = useRef();
  const skyTimer = useRef(0);
  const skyHeight = useRef(0);
  const portalRef = useRef();
  const cyberRef = useRef();
  const cyberRingRefs = useRef([]);
  const portalTokenRefs = useRef([]);
  const portalTimer = useRef(0);
  const returnSafetyTimer = useRef(0);
  const wasSpecialMode = useRef(false);

  const rareTokenMeta = useRef({
    z: -650 - Math.random() * 400,
    lane: Math.floor(Math.random() * LANES.length),
    live: true,
  });

  const portalMeta = useRef({
    z: -620 - Math.random() * 500,
    lane: Math.floor(Math.random() * LANES.length),
    live: true,
  });

  const portalTokenMeta = useRef(
    Array.from({ length: PORTAL_TOKEN_POOL }, (_, i) => ({
      z: -18 - i * 9.2,
      lane: i % LANES.length,
      live: true,
    }))
  );

  const skyArrowMeta = useRef({
    z: -900 - Math.random() * 650,
    lane: Math.floor(Math.random() * LANES.length),
    live: true,
  });

  const skyTokenMeta = useRef(
    Array.from({ length: SKY_TOKEN_POOL }, (_, i) => ({
      z: -20 - i * 10.5,
      lane: i % LANES.length,
      live: true,
    }))
  );

  const enemyRefs = useRef([]);
  const enemyBodyRefs = useRef([]);
  const cityRefs = useRef([]);
  const lampRefs = useRef([]);
  const playerX = useRef(0);
  const worldDistance = useRef(0);
  const currentSpeed = useRef(0);
  const lastHud = useRef(0);
  const collisionCooldown = useRef(0);
  const truckRef = useRef();
  const jumpTokenRefs = useRef([]);
  const isJumping = useRef(false);
  const jumpTime = useRef(0);
  const truckCooldown = useRef(0);
  const nitroPickupRefs = useRef([]);
  const nitroTimer = useRef(0);
  const policeRef = useRef();
  const tunnelRef = useRef();
  const bridgeRef = useRef();
  const policeActive = useRef(false);
  const lastPowerHudUpdate = useRef(0);
  const idleBlend = useRef(1);
  const carVisualScale = useRef(1);
  const roadWorksRef = useRef();
  const crossingRefs = useRef([]);
  const crossingMeta = useRef(
    Array.from({ length: CROSSING_POOL }, (_, i) => ({
      z:
        -320 -
        i * 420 -
        Math.random() * 120,
    }))
  );

  const truckMeta = useRef({
    z: -150,
    lane: 1,
  });


  const nitroMeta = useRef(Array.from({ length: POWERUP_POOL / 2 }, (_, i) => ({
    z: -280 - i * 300 - Math.random() * 150,
    lane: (i + 1) % LANES.length,
    live: true,
  })));

  const tunnelMeta = useRef({ z: -420 });
  const bridgeMeta = useRef({ z: -760 });
  const roadWorksMeta = useRef({
    z: ROAD_WORKS_START_Z,
    lane: Math.floor(Math.random() * LANES.length),
  });

  const jumpTokenOffsets = useMemo(() => [
    { z: 4.6, y: 0.8, live: true },
    { z: 2.8, y: 1.35, live: true },
    { z: 1.0, y: 1.95, live: true },
    { z: -0.8, y: 2.35, live: true },
    { z: -2.6, y: 2.15, live: true },
    { z: -4.4, y: 1.65, live: true },
    { z: -6.2, y: 1.05, live: true },
  ], []);
  const tokenMeta = useRef(Array.from({ length: TOKEN_POOL }, (_, i) => ({
    z: -22 - i * 14 - Math.random() * 12,
    lane: i % LANES.length,
    live: true,
  })));
  const enemyMeta = useRef(Array.from({ length: ENEMY_POOL }, (_, i) => ({
    z: -110 - i * 90 - Math.random() * 45,
    lane: (i + 1) % LANES.length,
    speedBias: 0.55 + Math.random() * 0.25,
  })));
  const enemyColors = useMemo(() => [
    '#d9dde7',
    '#d95b68',
    '#4f86bd',
    '#bfc0c4',
    '#7463b5',
    '#c58a45',
    '#3e8d86',
    '#a65778',
  ], []);

  useEffect(() => {
    currentSpeed.current = Math.max(0, Math.min(currentSpeed.current, activeCar.maxSpeed));
  }, [activeCar.maxSpeed]);

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.042);
    nitroTimer.current = Math.max(0, nitroTimer.current - dt);
    skyTimer.current = Math.max(0, skyTimer.current - dt);
    portalTimer.current = Math.max(0, portalTimer.current - dt);
    const skyActive = skyTimer.current > 0;
    const portalActive = portalTimer.current > 0;

    const specialModeActive = skyActive || portalActive;

    // After SKY RUN / CYBER RUN ends, keep collisions disabled briefly
    // and move traffic away so the player cannot return inside an obstacle.
    if (wasSpecialMode.current && !specialModeActive) {
      returnSafetyTimer.current = 1.5;

      enemyMeta.current.forEach((enemy, i) => {
        enemy.z = -100 - i * 55 - Math.random() * 80;
      });

      truckMeta.current.z = -180 - Math.random() * 80;
    }

    wasSpecialMode.current = specialModeActive;

    returnSafetyTimer.current = Math.max(
      0,
      returnSafetyTimer.current - dt
    );

    const returnSafetyActive = returnSafetyTimer.current > 0;

    skyHeight.current = THREE.MathUtils.damp(
      skyHeight.current,
      skyActive ? SKY_HEIGHT : 0,
      skyActive ? 3.1 : 2.0,
      dt
    );

    const nitroBoost = nitroTimer.current > 0 ? 1.42 : 1;
    const portalBoost = portalActive ? 2 : 1;
    const targetSpeed = running ? activeCar.maxSpeed * 0.3 * nitroBoost * portalBoost : 0;
    const accel = running ? 0.88 : 1.8;
    currentSpeed.current = THREE.MathUtils.damp(currentSpeed.current, targetSpeed, accel, dt);
    if (!running && currentSpeed.current < 0.7) currentSpeed.current = 0;

    const speed01 = currentSpeed.current / Math.max(1, activeCar.maxSpeed);
    const metersPerFrame = speed01 * 30 * dt;
    worldDistance.current += metersPerFrame;

    const safeLaneTarget = THREE.MathUtils.clamp(
      Number.isFinite(laneTarget) ? laneTarget : 1,
      0,
      LANES.length - 1
    );

    const idleTarget =
      !running && currentSpeed.current < 3 ? 1 : 0;

    idleBlend.current = THREE.MathUtils.damp(
      idleBlend.current,
      idleTarget,
      7.5,
      dt
    );

    const raceLaneX = LANES[safeLaneTarget];
    const targetLaneX = THREE.MathUtils.lerp(
      raceLaneX,
      0,
      idleBlend.current
    );

    const steerDistance =
      targetLaneX - playerX.current;

    playerX.current = THREE.MathUtils.damp(
      playerX.current,
      targetLaneX,
      idleBlend.current > 0.05 ? 6.5 : 8.5,
      dt
    );


    if (carGroup.current) {
      const idle = idleBlend.current;

      carGroup.current.position.x =
        playerX.current;

      carGroup.current.position.z = THREE.MathUtils.lerp(
        1.6,
        -0.2,
        idle
      );

      carVisualScale.current = THREE.MathUtils.damp(
        carVisualScale.current,
        THREE.MathUtils.lerp(1, 2, idle),
        7.5,
        dt
      );

      carGroup.current.scale.setScalar(
        carVisualScale.current
      );

      const baseYaw = Math.PI;

      const steerYaw =
        THREE.MathUtils.clamp(
          steerDistance * -0.12,
          -0.22,
          0.22
        );

      const targetYaw =
        baseYaw + steerYaw;

      carGroup.current.rotation.y =
        THREE.MathUtils.damp(
          carGroup.current.rotation.y,
          targetYaw,
          7,
          dt
        );

      carGroup.current.rotation.z =
        THREE.MathUtils.damp(
          carGroup.current.rotation.z,
          steerDistance *
            -0.045 *
            (1 - idleBlend.current),
          7,
          dt
        );

      let carY =
        THREE.MathUtils.lerp(
          0.03 +
            Math.sin(state.clock.elapsedTime * 5.5) *
              0.008 *
              speed01,
          0.08,
          idleBlend.current
        );

      if (isJumping.current) {
        jumpTime.current += dt;

        const jumpDuration = 1.15;
        const progress = jumpTime.current / jumpDuration;

        if (progress >= 1) {
          isJumping.current = false;
          jumpTime.current = 0;
          carY = 0.03;
        } else {
          carY =
            0.03 +
            Math.sin(progress * Math.PI) * 2.35;
        }
      }

      carY += skyHeight.current;
      carGroup.current.position.y = carY;

      if (isJumping.current) {
        const progress = jumpTime.current / 1.15;

        carGroup.current.rotation.x =
          THREE.MathUtils.damp(
            carGroup.current.rotation.x,
            Math.sin(progress * Math.PI * 2) * 0.07,
            8,
            dt
          );
      } else {
        carGroup.current.rotation.x =
          THREE.MathUtils.damp(
            carGroup.current.rotation.x,
            0,
            10,
            dt
          );
      }
    }

    const phase = worldDistance.current * 8;
    for (let i = 0; i < SEGMENTS; i += 1) {
      const seg = roadRefs.current[i];
      const lane = laneRefs.current[i];
      if (!seg) continue;
      let z = -i * SEGMENT_LENGTH + ((worldDistance.current * 8) % SEGMENT_LENGTH);
      if (z > 11) z -= SEGMENTS * SEGMENT_LENGTH;
      const x = curveAt(z, phase);
      const nextX = curveAt(z - 1, phase);
      seg.position.set(x, 0, z);
      seg.rotation.y = Math.atan2(nextX - x, 1);
      if (lane) {
        lane.position.copy(seg.position);
        lane.rotation.copy(seg.rotation);
      }
    }

    tokenMeta.current.forEach((item, i) => {
      const token = tokenRefs.current[i];
      if (!token) return;
      item.z += metersPerFrame * 9.5;
      if (item.z > 8) {
        item.z = -210 - Math.random() * 130;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.live = true;
      }
      const cx = curveAt(item.z, phase);
      token.position.set(cx + LANES[item.lane], 0.46, item.z);
      token.rotation.y += dt * 2.6;
      token.rotation.z = Math.sin(state.clock.elapsedTime * 2.8 + i) * 0.2;
      token.visible = item.live && currentSpeed.current > 12;
      if (!skyActive &&
        !portalActive && item.live && currentSpeed.current > 12 && item.z > -2.2 && item.z < 1.6 && Math.abs(token.position.x - playerX.current) < 0.95) {
        item.live = false;
        onToken();
      }
    });

    // Rare Token
    if (rareTokenRef.current) {
      const token = rareTokenRef.current;
      const item = rareTokenMeta.current;

      item.z += metersPerFrame * 9.5;

      if (item.z > 8) {
        // Следующий rare token далеко впереди
        item.z = -650 - Math.random() * 550;
        item.lane = Math.floor(
          Math.random() * LANES.length
        );
        item.live = true;
      }

      const cx = curveAt(item.z, phase);

      token.position.set(
        cx + LANES[item.lane],
        0.52,
        item.z
      );

      token.rotation.y += dt * 3.5;
      token.rotation.z =
        Math.sin(state.clock.elapsedTime * 3.2) * 0.15;

      token.visible =
        item.live &&
        currentSpeed.current > 12;

      if (
        !skyActive &&
        !portalActive &&
        item.live &&
        currentSpeed.current > 12 &&
        item.z > -2.2 &&
        item.z < 1.6 &&
        Math.abs(
          token.position.x - playerX.current
        ) < 0.95
      ) {
        item.live = false;

        // +10
        onToken(10);
      }
    }

    // SKY RUN pickup: rare turquoise arrow that launches the car above the city.
    if (skyArrowRef.current) {
      const pickup = skyArrowRef.current;
      const item = skyArrowMeta.current;

      item.z += metersPerFrame * 8.2;

      if (item.z > 10) {
        item.z = -900 - Math.random() * 750;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.live = true;
      }

      const cx = curveAt(item.z, phase);
      pickup.position.set(cx + LANES[item.lane], 0.62, item.z);
      pickup.rotation.y += dt * 2.7;
      pickup.position.y = 0.68 + Math.sin(state.clock.elapsedTime * 4.5) * 0.08;
      pickup.visible = item.live && !skyActive &&
        !portalActive && currentSpeed.current > 12;

      if (
        item.live &&
        !skyActive &&
        !portalActive &&
        currentSpeed.current > 12 &&
        item.z > -2.1 &&
        item.z < 1.9 &&
        Math.abs(pickup.position.x - playerX.current) < 1.05
      ) {
        item.live = false;
        skyTimer.current = SKY_RUN_DURATION;
        nitroTimer.current = 0;

        // Refill the bonus coin field every time Sky Run starts.
        skyTokenMeta.current.forEach((token, i) => {
          token.z = -18 - i * 10.5;
          token.lane = i % LANES.length;
          token.live = true;
        });

        onPowerState?.({
          type: 'sky',
          nitro: 0,
          sky: SKY_RUN_DURATION,
        });
      }
    }

    // Three times the normal token count, distributed over all three lanes.
    skyTokenMeta.current.forEach((item, i) => {
      const token = skyTokenRefs.current[i];
      if (!token) return;

      item.z += metersPerFrame * 10.2;

      if (item.z > 8) {
        item.z = -165 - Math.random() * 95;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.live = true;
      }

      const cx = curveAt(item.z, phase);
      token.position.set(
        cx + LANES[item.lane],
        SKY_HEIGHT + 0.48,
        item.z
      );
      token.rotation.y += dt * 3.4;
      token.rotation.z = Math.sin(state.clock.elapsedTime * 3 + i) * 0.16;
      token.visible = skyActive && item.live;

      if (
        skyActive &&
        item.live &&
        item.z > -2.2 &&
        item.z < 1.7 &&
        Math.abs(token.position.x - playerX.current) < 0.95
      ) {
        item.live = false;
        onToken();
      }
    });

    if (planetRef.current) {
      planetRef.current.visible = skyHeight.current > 4;
      planetRef.current.rotation.y += dt * 0.035;
    }

    // NEON PORTAL pickup and 8-second cyberspace bonus run.
    if (portalRef.current) {
      const pickup = portalRef.current;
      const item = portalMeta.current;
      item.z += metersPerFrame * 8.0;
      if (item.z > 10) {
        item.z = -1200 - Math.random() * 1200;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.live = true;
      }
      pickup.position.set(LANES[item.lane], 1.5, item.z);
      pickup.rotation.z += dt * 1.8;
      pickup.rotation.y = Math.sin(state.clock.elapsedTime * 2.2) * 0.12;
      pickup.visible = item.live && !skyActive &&
        !portalActive && currentSpeed.current > 12;
      if (item.live && !skyActive &&
        !portalActive && currentSpeed.current > 12 && item.z > -2.2 && item.z < 1.9 && Math.abs(pickup.position.x - playerX.current) < 1.15) {
        item.live = false;
        portalTimer.current = PORTAL_DURATION;
        nitroTimer.current = 0;
        portalTokenMeta.current.forEach((token, i) => {
          token.z = -16 - i * 9.2;
          token.lane = i % LANES.length;
          token.live = true;
        });
        onPowerState?.({ type: 'portal', nitro: 0, portal: PORTAL_DURATION });
      }
    }

    if (cyberRef.current) cyberRef.current.visible = portalActive;
    cyberRingRefs.current.forEach((ring, i) => {
      if (!ring) return;
      let z = -8 - i * 13 + ((worldDistance.current * 18) % (CYBER_RING_POOL * 13));
      if (z > 8) z -= CYBER_RING_POOL * 13;
      ring.position.set(0, 0, z);
      ring.visible = portalActive;
    });

    portalTokenMeta.current.forEach((item, i) => {
      const token = portalTokenRefs.current[i];
      if (!token) return;
      item.z += metersPerFrame * 16.5;
      if (item.z > 8) {
        item.z = -155 - Math.random() * 75;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.live = true;
      }
      token.position.set(LANES[item.lane], 0.52, item.z);
      token.rotation.y += dt * 4.5;
      token.visible = portalActive && item.live;
      if (portalActive && item.live && item.z > -2.2 && item.z < 1.7 && Math.abs(token.position.x - playerX.current) < 0.95) {
        item.live = false;
        onToken();
      }
    });

    nitroMeta.current.forEach((item, i) => {
      const pickup = nitroPickupRefs.current[i];
      if (!pickup) return;

      item.z += metersPerFrame * 8.4;
      if (item.z > 10) {
        item.z = -500 - Math.random() * 360;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.live = true;
      }

      const cx = curveAt(item.z, phase);
      pickup.position.set(cx + LANES[item.lane], 0.7, item.z);
      pickup.rotation.y += dt * 2.8;
      pickup.rotation.z = Math.sin(state.clock.elapsedTime * 4 + i) * 0.12;
      pickup.visible = item.live && !skyActive &&
        !portalActive && currentSpeed.current > 12;

      if (
        item.live &&
        !skyActive &&
        !portalActive &&
        currentSpeed.current > 12 &&
        item.z > -2.0 &&
        item.z < 2.0 &&
        Math.abs(pickup.position.x - playerX.current) < 1.05
      ) {
        item.live = false;
        nitroTimer.current = NITRO_DURATION;
        onPowerState?.({
          type: 'nitro',
          nitro: NITRO_DURATION,
        });
      }
    });


    // Tunnel
    if (tunnelRef.current) {
      const meta = tunnelMeta.current;
      meta.z += metersPerFrame * 5.5;
      if (meta.z > 26) meta.z = -760 - Math.random() * 300;

      const cx = curveAt(meta.z, phase);
      tunnelRef.current.position.set(cx, 0, meta.z);
      tunnelRef.current.rotation.y = Math.atan2(curveAt(meta.z - 1, phase) - cx, 1);
      tunnelRef.current.visible = !portalActive && currentSpeed.current > 10;
    }

    // Bridge
    if (bridgeRef.current) {
      const meta = bridgeMeta.current;
      meta.z += metersPerFrame * 5.2;
      if (meta.z - BRIDGE_LENGTH / 2 > 30) {
        meta.z = -950 - Math.random() * 380;
      }

      const cx = curveAt(meta.z, phase);
      bridgeRef.current.position.set(cx, 0, meta.z);
      bridgeRef.current.rotation.y = Math.atan2(curveAt(meta.z - 1, phase) - cx, 1);
      bridgeRef.current.visible = !portalActive && currentSpeed.current > 10;
    }

    // Road Works
    if (roadWorksRef.current) {
      const meta = roadWorksMeta.current;

      meta.z += metersPerFrame * 5.8;

      // Когда весь 300-метровый участок полностью остался позади.
      if (
        meta.z - ROAD_WORKS_LENGTH / 2 > 30
      ) {
        meta.z = -850 - Math.random() * 500;

        // Следующий ремонт уже на случайной полосе.
        meta.lane = Math.floor(
          Math.random() * LANES.length
        );
      }

      roadWorksRef.current.position.set(
        LANES[meta.lane],
        0,
        meta.z
      );

      roadWorksRef.current.visible =
        !portalActive && currentSpeed.current > 10;

      // Collision with road works
      const roadWorksLaneX = LANES[meta.lane];

      const sameLane =
        Math.abs(roadWorksLaneX - playerX.current) < 1.2;

      // Позиции барьеров относительно центра RoadWorks.
      // Соответствуют [40, 100, 160, 220] из RoadWorks().
      const barrierOffsets = [40, 100, 160, 220];

      const hitBarrier = barrierOffsets.some((offset) => {
        const barrierZ =
          meta.z + ROAD_WORKS_LENGTH / 2 - offset;

        return (
          barrierZ > -2.3 &&
          barrierZ < 1.8
        );
      });

      if (
        !skyActive &&
        !portalActive &&
        !returnSafetyActive &&
        !isJumping.current &&
        sameLane &&
        hitBarrier &&
        currentSpeed.current > 10 &&
        collisionCooldown.current <= 0
      ) {
        collisionCooldown.current = 1.15;

        // Такой же удар, как об машину
        currentSpeed.current *= 0.52;

        // Сбиваем Nitro
        nitroTimer.current = 0;

        onPowerState?.({
          type: 'reset',
          nitro: 0,
        });

        onCrash();
      }
    }

    // Pedestrian crossings
    crossingMeta.current.forEach((item, i) => {
      const crossing = crossingRefs.current[i];
      if (!crossing) return;

      item.z += metersPerFrame * 8;

      if (item.z > 12) {
        item.z =
          -700 -
          Math.random() * 400;
      }

      crossing.position.set(
        0,
        0,
        item.z
      );

      crossing.visible =
        !portalActive && currentSpeed.current > 8;
    });

    // Police starts chasing after the player has driven far enough.
    //policeActive.current = worldDistance.current > POLICE_DISTANCE;

    /*if (policeRef.current) {
      const policeZ = 6.6;
      const policeX = THREE.MathUtils.damp(
        policeRef.current.position.x,
        playerX.current * 0.82,
        3.5,
        dt
      );

      policeRef.current.position.set(policeX, 0.02, policeZ);
      policeRef.current.rotation.y = Math.PI;
      policeRef.current.visible =
        policeActive.current &&
        running &&
        currentSpeed.current > 20;

      const pulse = Math.sin(state.clock.elapsedTime * 12) > 0 ? 1 : 0.25;
      policeRef.current.scale.setScalar(1 + pulse * 0.012);
    }*/

    enemyMeta.current.forEach((item, i) => {
      const enemy = enemyRefs.current[i];
      if (!enemy) return;
      item.z += metersPerFrame * (6.6 + item.speedBias * 2.4);
      if (item.z > 10) {
        item.z = -260 - Math.random() * 240;
        item.lane = Math.floor(Math.random() * LANES.length);
        item.speedBias = 0.5 + Math.random() * 0.35;

        const truckMetaNow = truckMeta.current;
        if (
          item.lane === truckMetaNow.lane &&
          Math.abs(item.z - truckMetaNow.z) < 32
        ) {
          item.z =
            truckMetaNow.z -
            46 -
            Math.random() * 40;
        }

        keepEnemySeparated(
          enemyMeta.current,
          i
        );
      }

      const truckMetaNow = truckMeta.current;
      if (
        item.lane === truckMetaNow.lane &&
        Math.abs(item.z - truckMetaNow.z) < 28
      ) {
        item.z =
          item.z >= truckMetaNow.z
            ? truckMetaNow.z + 32
            : truckMetaNow.z - 32;
      }

      // Cars must not visually merge into each other.
      // Check this continuously because their speedBias values differ.
      keepEnemySeparated(
        enemyMeta.current,
        i
      );

      const cx = curveAt(item.z, phase);
      enemy.position.set(cx + LANES[item.lane], 0.02, item.z);
      enemy.rotation.y = Math.atan2(curveAt(item.z - 1, phase) - cx, 1);
      enemy.visible = !skyActive &&
        !portalActive && currentSpeed.current > 18;
      const enemyNearTruck =
        item.lane === truckMeta.current.lane &&
        Math.abs(item.z - truckMeta.current.z) < 30;

      if (
        !skyActive &&
        !portalActive &&
        !returnSafetyActive &&
        !isJumping.current &&
        !enemyNearTruck &&
        currentSpeed.current > 18 &&
        collisionCooldown.current <= 0 &&
        item.z > -2.5 &&
        item.z < 1.8 &&
        Math.abs(enemy.position.x - playerX.current) < 1.25
      ) {
        collisionCooldown.current = 1.15;
        currentSpeed.current *= 0.52;
        nitroTimer.current = 0;

        onPowerState?.({
          type: 'reset',
          nitro: 0,
        });

        onCrash();

        item.z = -220 - Math.random() * 160;
        item.lane = Math.floor(Math.random() * LANES.length);

        if (
          item.lane === truckMeta.current.lane &&
          Math.abs(item.z - truckMeta.current.z) < 32
        ) {
          item.z =
            truckMeta.current.z -
            48 -
            Math.random() * 30;
        }

        keepEnemySeparated(
          enemyMeta.current,
          i
        );
      }
    });


    const truck = truckRef.current;

    if (truck) {
      const meta = truckMeta.current;

      meta.z += metersPerFrame * 4.5;

      if (meta.z > 14) {
        meta.z = -260 - Math.random() * 60;
        meta.lane = Math.floor(Math.random() * LANES.length);
        truckCooldown.current = 0;

        jumpTokenOffsets.forEach((token) => {
          token.live = true;
        });
      }

      const cx = curveAt(meta.z, phase);

      truck.position.set(
        cx + LANES[meta.lane],
        0,
        meta.z
      );

      truck.rotation.y =
        Math.atan2(
          curveAt(meta.z - 1, phase) - cx,
          1
        );

      truck.visible = !portalActive && currentSpeed.current > 12;

      truckCooldown.current = Math.max(
        0,
        truckCooldown.current - dt
      );

      const sameLane =
        Math.abs(LANES[meta.lane] - playerX.current) < 1.15;

      // Игрок стоит примерно на z = 1.6.
      // Рампа грузовика выступает вперед примерно на 4.5 от центра грузовика.
      const playerZ = 1.6;
      const rampTipZ = meta.z + 4.5;

      // Прыжок начинается именно когда край рампы касается машины.
      const enteringRamp =
        rampTipZ > playerZ - 3 &&
        rampTipZ < playerZ + 0.8;

      if (
        !skyActive &&
        !portalActive &&
        !returnSafetyActive &&
        !isJumping.current &&
        truckCooldown.current <= 0 &&
        currentSpeed.current > 12 &&
        sameLane &&
        enteringRamp
      ) {
        isJumping.current = true;
        jumpTime.current = 0;
        truckCooldown.current = 1.5;
      }
    }

    jumpTokenOffsets.forEach((item, i) => {
      const token = jumpTokenRefs.current[i];
      const truck = truckRef.current;

      if (!token || !truck) return;

      const meta = truckMeta.current;
      const tokenZ = meta.z + item.z;
      const cx = curveAt(tokenZ, phase);

      token.position.set(
        cx + LANES[meta.lane],
        item.y,
        tokenZ
      );

      token.rotation.y += dt * 3;
      token.rotation.z =
        Math.sin(
          state.clock.elapsedTime * 3 + i
        ) * 0.18;

      token.visible =
        item.live &&
        currentSpeed.current > 12;

      const playerY =
        carGroup.current
          ? carGroup.current.position.y
          : 0;

      if (
        item.live &&
        currentSpeed.current > 12 &&
        tokenZ > -0.7 &&
        tokenZ < 2.7 &&
        Math.abs(
          token.position.x -
            playerX.current
        ) < 1.0 &&
        Math.abs(
          item.y -
            playerY
        ) < 0.95
      ) {
        item.live = false;
        onToken();
      }
    });

    collisionCooldown.current = Math.max(0, collisionCooldown.current - dt);

    const powerHudNow = state.clock.elapsedTime;
    if (
      onPowerState &&
      powerHudNow - lastPowerHudUpdate.current > 0.1
    ) {
      lastPowerHudUpdate.current = powerHudNow;

      onPowerState({
        type: null,
        nitro: nitroTimer.current,
        sky: skyTimer.current,
        portal: portalTimer.current,
      });
    }

    cityRefs.current.forEach((block, i) => {
      if (!block) return;
      const side = i % 2 === 0 ? -1 : 1;
      const pair = Math.floor(i / 2);
      let z = -18 - pair * 31 + ((worldDistance.current * 5.3) % (31 * Math.ceil(CITY_POOL / 2)));
      if (z > 16) z -= 31 * Math.ceil(CITY_POOL / 2);
      const cx = curveAt(z, phase);
      block.position.set(cx + side * (ROAD_WIDTH / 2 + 4.6 + (i % 3) * 1.4), -0.08, z);
      block.rotation.y = Math.atan2(curveAt(z - 1, phase) - cx, 1);
      block.visible = !portalActive;
    });

    lampRefs.current.forEach((lamp, i) => {
      if (!lamp) return;
      const side = i % 2 === 0 ? -1 : 1;
      const pair = Math.floor(i / 2);
      let z = -10 - pair * 24 + ((worldDistance.current * 7) % (24 * 10));
      if (z > 15) z -= 240;
      const cx = curveAt(z, phase);
      lamp.position.set(cx + side * (ROAD_WIDTH / 2 + 0.8), 0, z);
      lamp.rotation.y = Math.atan2(curveAt(z - 1, phase) - cx, 1);
      lamp.visible = !portalActive;
    });

    if (root.current) root.current.rotation.z = THREE.MathUtils.damp(root.current.rotation.z, (playerX.current / 12) * -0.02, 6, dt);

    state.scene.fog.near = skyActive ? 80 : (portalActive ? 35 : (quality === 'low' ? 18 : 24));
    state.scene.fog.far = skyActive ? 185 : (portalActive ? 170 : (quality === 'low' ? 105 : 150));
    state.scene.background.set(portalActive ? '#010006' : '#02040a');
    state.scene.fog.color.set(portalActive ? '#070012' : '#02040a');

    const idle = idleBlend.current;

    cameraX.current = THREE.MathUtils.damp(
      cameraX.current,
      THREE.MathUtils.lerp(
        playerX.current * 0.55,
        0,
        idle
      ),
      5.5,
      dt
    );

    state.camera.position.x = cameraX.current;

    const raceCameraY = 5.2 + speed01 * 0.35 + skyHeight.current;
    const idleCameraY = 4.45;

    state.camera.position.y = THREE.MathUtils.damp(
      state.camera.position.y,
      THREE.MathUtils.lerp(
        raceCameraY,
        idleCameraY,
        idle
      ),
      7.5,
      dt
    );

    const raceCameraZ =
      skyActive ? 11.8 : (nitroTimer.current > 0 ? 10.8 : 9.6);
    const idleCameraZ = 13.2;

    state.camera.position.z = THREE.MathUtils.damp(
      state.camera.position.z,
      THREE.MathUtils.lerp(
        raceCameraZ,
        idleCameraZ,
        idle
      ),
      7.5,
      dt
    );

    const raceFov =
      portalActive ? 58 : (nitroTimer.current > 0 ? 53 : 46);
    const idleFov = 44;

    const nextFov = THREE.MathUtils.damp(
      state.camera.fov,
      THREE.MathUtils.lerp(
        raceFov,
        idleFov,
        idle
      ),
      7.5,
      dt
    );

    if (Math.abs(nextFov - state.camera.fov) > 0.01) {
      state.camera.fov = nextFov;
      state.camera.updateProjectionMatrix();
    }

    state.camera.lookAt(
      THREE.MathUtils.lerp(
        playerX.current * 0.28,
        0,
        idle
      ),
      0.55 + skyHeight.current,
      THREE.MathUtils.lerp(
        -9.5,
        -0.6,
        idle
      )
    );

    const now = state.clock.elapsedTime;
    if (now - lastHud.current > 0.11) {
      lastHud.current = now;
      onSpeed(Math.round(currentSpeed.current));
    }

  });

  return (
    <group ref={root}>
      <ambientLight intensity={0.95} color="#7aa0d7" />
<hemisphereLight
  intensity={0.8}
  color="#7cf8ff"
  groundColor="#05070f"
/>
      <directionalLight position={[1.5, 7, 6]} intensity={1.7} color="#e8f7ff" />
      <pointLight position={[0, 4.5, 3]} intensity={18} distance={24} color="#fb2d67" />

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.14, -80]}>
        <planeGeometry args={[150, 360]} />
        <meshStandardMaterial color="#03050a" roughness={1} />
      </mesh>

      {Array.from({ length: SEGMENTS }).map((_, i) => (
        <React.Fragment key={i}>
          <mesh ref={(el) => { roadRefs.current[i] = el; }} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[ROAD_WIDTH, SEGMENT_LENGTH + 0.25]} />
            <meshStandardMaterial
              color={i % 2 === 0 ? '#181e29' : '#151b25'}
              roughness={0.94}
              metalness={0.02}
            />
          </mesh>
          <group ref={(el) => { laneRefs.current[i] = el; }}>
            {[-1.25, 1.25].map((x) => (
              <mesh key={x} position={[x, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <planeGeometry args={[0.08, 3.2]} />
                <meshBasicMaterial color="#8390a7" transparent opacity={0.52} />
              </mesh>
            ))}
            <mesh position={[-ROAD_WIDTH / 2 + 0.18, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <planeGeometry args={[0.07, SEGMENT_LENGTH]} />
              <meshBasicMaterial color="#5f6b7b" transparent opacity={0.55} />
            </mesh>
            <mesh position={[ROAD_WIDTH / 2 - 0.18, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <planeGeometry args={[0.07, SEGMENT_LENGTH]} />
              <meshBasicMaterial color="#5f6b7b" transparent opacity={0.55} />
            </mesh>
          </group>
        </React.Fragment>
      ))}

      {Array.from({ length: CITY_POOL }).map((_, i) => (
        <group key={i} ref={(el) => { cityRefs.current[i] = el; }}>
          <CityBlock index={i} />
        </group>
      ))}

      {Array.from({ length: 14 }).map((_, i) => (
        <group key={i} ref={(el) => { lampRefs.current[i] = el; }}>
          <Lamp side={i % 2 === 0 ? -1 : 1} />
        </group>
      ))}

      {Array.from({ length: TOKEN_POOL }).map((_, i) => (
        <Token key={i} tokenRef={(el) => { tokenRefs.current[i] = el; }} />
      ))}

      {Array.from({ length: ENEMY_POOL }).map((_, i) => (
        <EnemyCar
          key={i}
          carRef={(el) => {
            enemyRefs.current[i] = el;
          }}
          color={enemyColors[i % enemyColors.length]}
        />
      ))}

      <RareToken tokenRef={rareTokenRef} />

      <SkyArrowPickup pickupRef={skyArrowRef} />
      <NeonPortal portalRef={portalRef} />
      <CyberSpace cyberRef={cyberRef} ringRefs={cyberRingRefs} />
      {Array.from({ length: PORTAL_TOKEN_POOL }).map((_, i) => (
        <CyberToken key={`portal-token-${i}`} tokenRef={(el) => { portalTokenRefs.current[i] = el; }} />
      ))}

      {Array.from({ length: SKY_TOKEN_POOL }).map((_, i) => (
        <SkyToken
          key={`sky-token-${i}`}
          tokenRef={(el) => {
            skyTokenRefs.current[i] = el;
          }}
        />
      ))}

      <SpacePlanet planetRef={planetRef} />

      {Array.from({ length: POWERUP_POOL / 2 }).map((_, i) => (
        <NitroPickup
          key={`nitro-${i}`}
          pickupRef={(el) => {
            nitroPickupRefs.current[i] = el;
          }}
        />
      ))}

      <Tunnel tunnelRef={tunnelRef} />
      <Bridge bridgeRef={bridgeRef} />
      {/*<PoliceCar policeRef={policeRef} />*/}
      <RoadWorks roadWorksRef={roadWorksRef} />
      {Array.from({ length: CROSSING_POOL }).map((_, i) => (
        <PedestrianCrossing
          key={`crossing-${i}`}
          crossingRef={(el) => {
            crossingRefs.current[i] = el;
          }}
        />
      ))}

      <JumpTruck truckRef={truckRef} />

      {jumpTokenOffsets.map((_, i) => (
        <Token
          key={`jump-token-${i}`}
          tokenRef={(el) => {
            jumpTokenRefs.current[i] = el;
          }}
        />
      ))}

      <group ref={carGroup} position={[0, 0.02, 1.6]} rotation={[0, Math.PI, 0]}>
        <Suspense fallback={null}>
          <CarModel
            modelPath={activeCar.modelPath}
            color={activeColor}
            scale={(activeCar.raceScale || activeCar.scale) * 0.67}
          />
        </Suspense>
        <pointLight position={[-0.62, 0.48, 1.18]} intensity={3.8} distance={7} color="#fff6d0" />
        <pointLight position={[0.62, 0.48, 1.18]} intensity={3.8} distance={7} color="#fff6d0" />
        <pointLight position={[-0.45, 0.3, -1.0]} intensity={0.8} distance={4} color="#3ee7ff" />
        <pointLight position={[0.45, 0.3, -1.0]} intensity={0.8} distance={4} color="#3ee7ff" />
      </group>
    </group>
  );
}

export default function RaceWorld(props) {
  const dpr = props.quality === 'low' ? [0.7, 0.9] : [1, 1.2];

  const [powerState, setPowerState] = useState({
    nitro: 0,
    sky: 0,
    portal: 0,
  });


  const handlePowerState = (next) => {
    setPowerState((prev) => ({
      nitro:
        typeof next.nitro === 'number'
          ? next.nitro
          : prev.nitro,
      sky:
        typeof next.sky === 'number'
          ? next.sky
          : prev.sky,
      portal:
        typeof next.portal === 'number'
          ? next.portal
          : prev.portal,
    }));
  };

  const activePills = [];


  if (powerState.nitro > 0.05) {
    activePills.push({
      key: 'nitro',
      icon: 'N₂O',
      iconBg: 'rgba(49, 255, 34, 0.18)',
      iconBorder: 'rgba(67, 255, 61, 0.85)',
      iconColor: '#43FF3D',
      label: 'NITRO',
      value: `${Math.ceil(powerState.nitro)}s`,
    });
  }

  if (powerState.sky > 0.05) {
    activePills.push({
      key: 'sky',
      icon: '↑',
      iconBg: 'rgba(67, 247, 255, 0.18)',
      iconBorder: 'rgba(67, 247, 255, 0.9)',
      iconColor: '#43F7FF',
      label: 'SKY RUN',
      value: `${Math.ceil(powerState.sky)}s`,
    });
  }

  if (powerState.portal > 0.05) {
    activePills.push({
      key: 'portal',
      icon: '◉',
      iconBg: 'rgba(195, 68, 255, 0.18)',
      iconBorder: 'rgba(195, 68, 255, 0.9)',
      iconColor: '#C344FF',
      label: 'CYBER RUN',
      value: `${Math.ceil(powerState.portal)}s`,
    });
  }


  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Canvas
        className="race-canvas"
        dpr={dpr}
        gl={{
          antialias: props.quality !== 'low',
          powerPreference: 'high-performance',
          alpha: false,
        }}
        camera={{
          position: [0, 5.25, 9.6],
          fov: 46,
          near: 0.1,
          far: 190,
        }}
        shadows={false}
        frameloop="always"
        onCreated={({ gl, scene }) => {
          gl.outputColorSpace = THREE.SRGBColorSpace;
          gl.toneMapping =
            THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1.12;
          scene.background =
            new THREE.Color('#02040a');
          scene.fog =
            new THREE.Fog('#02040a', 24, 150);
        }}
      >
        <RaceScene
          {...props}
          onPowerState={handlePowerState}
        />
      </Canvas>

      {activePills.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 250,
            left: 16,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 7,
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          {activePills.map((item) => (
            <div
              key={item.key}
              style={{
                minWidth: 92,
                height: 34,
                padding: '0 11px',
                borderRadius: 17,
                background:
                  'rgba(8, 12, 20, 0.76)',
                border:
                  '1px solid rgba(255,255,255,0.16)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow:
                  '0 6px 20px rgba(0,0,0,0.22)',
                color: '#fff',
                fontFamily:
                  'Inter, system-ui, sans-serif',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 24,
                  borderRadius: 8,
                  background: item.iconBg,
                  border: `1px solid ${item.iconBorder}`,
                  color: item.iconColor,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize:
                    item.key === 'nitro'
                      ? 10
                      : 20,
                  fontWeight: 950,
                  lineHeight: 1,
                  letterSpacing:
                    item.key === 'nitro'
                      ? -0.4
                      : 0,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>

              <span>{item.label}</span>

              <span
                style={{
                  opacity: 0.78,
                  fontWeight: 800,
                }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
